
import React, { useState, useMemo, useEffect } from 'react';
import { Ad, VendorProfile, SubscriptionPlan, VendorStatus, Neighborhood, Category, UserRole, SystemConfig, ManagementRole, StaffMember, Promotion, PromotionType, ActivationRule } from './types';
import AdCard from './components/AdCard';
import EntrepreneurDashboard from './components/EntrepreneurDashboard';
import VendorRegistration from './components/VendorRegistration';
import AdminPanel from './components/AdminPanel';
import ProductModal from './components/ProductModal';
import FilterBar from './components/FilterBar';
import VendorProfileView from './components/VendorProfileView';
import InfoModal from './components/InfoModal';
import Logo from './components/Logo';
import Login from './components/Login';
import PromoModal from './components/PromoModal';
import { supabase } from './lib/supabase';

interface Toast {
  message: string;
  type: 'success' | 'error' | 'info';
}

const App: React.FC = () => {
  const [view, setView] = useState<'buyer' | 'dashboard' | 'register' | 'admin' | 'vendor' | 'login'>('buyer');
  const [currentUser, setCurrentUser] = useState<{id: string, role: UserRole, managementRole?: ManagementRole} | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<Toast | null>(null);
  const [showPromo, setShowPromo] = useState(true);
  
  const [systemConfig, setSystemConfig] = useState<SystemConfig>({
    maxAdsFree: 3,
    maxAdsPro: 50,
    supportWhatsapp: "5511999999999",
    marketplaceName: "Compre em Guarulhos",
    marketplaceDescription: "A vitrine digital definitiva para o comércio de Guarulhos.",
    instagramUrl: "",
    youtubeUrl: ""
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<string | null>(null);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [visibleCount, setVisibleCount] = useState(18); 
  
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [ads, setAds] = useState<Ad[]>([]);
  const [vendors, setVendors] = useState<VendorProfile[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [selectedAd, setSelectedAd] = useState<Ad | null>(null);
  const [viewingVendor, setViewingVendor] = useState<VendorProfile | null>(null);
  const [infoModalType, setInfoModalType] = useState<'terms' | 'privacy' | 'how-it-works' | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadData = async () => {
    try {
      const [catRes, neighRes, vendorRes, adsRes, staffRes, configRes, promoRes] = await Promise.all([
        supabase.from('categorias').select('*').order('nome'),
        supabase.from('bairros').select('*').order('nome'),
        supabase.from('perfis_vendedores').select('*'),
        supabase.from('anuncios').select('*').order('created_at', { ascending: false }),
        supabase.from('equipe_gestao').select('*'),
        supabase.from('configuracoes_sistema').select('*').eq('id', 1).single(),
        supabase.from('promocoes').select('*').order('created_at', { ascending: false })
      ]);

      if (catRes.data) setCategories(catRes.data.map((c: any) => ({ id: c.id, name: c.nome, icon: c.icone || '📁' })));
      if (neighRes.data) setNeighborhoods(neighRes.data.map((n: any) => ({ id: n.id, name: n.nome, is_airport_zone: n.zona_aeroporto })));
      
      if (vendorRes.data) {
        setVendors(vendorRes.data.map((v: any) => ({
          id: v.id,
          name: v.nome_loja,
          responsible: v.nome_responsavel,
          email: v.email_contato,
          password: v.password,
          neighborhood_id: v.bairro_id,
          instagram: v.instagram || '',
          slug: v.slug,
          bio: v.bio || '',
          whatsapp: v.whatsapp,
          category_id: v.categoria_id,
          avatar_url: v.avatar_url || `https://picsum.photos/seed/${v.id}/200`,
          status: v.status as VendorStatus,
          plan: v.plano as SubscriptionPlan,
          plan_expires_at: v.plan_expires_at,
          verified_until: v.verified_until,
          rating: Number(v.rating) || 5.0,
          location: '',
          created_at: v.created_at,
          role: UserRole.OWNER,
          email_verified: v.email_verified
        })));
      }

      if (adsRes.data) {
        setAds(adsRes.data.map((a: any) => ({
          id: a.id,
          vendor_id: a.vendedor_id,
          title: a.titulo || '',
          description: a.descricao || '',
          price: Number(a.preco) || null,
          price_on_request: a.preco_sob_consulta,
          category_id: a.categoria_id,
          images: a.imagens || [],
          tags: a.tags || [],
          is_active: a.is_active,
          clicks: a.clicks_contato || 0,
          views: a.visualizacoes || 0,
          created_at: a.created_at
        })));
      }

      if (staffRes.data) {
        setStaff(staffRes.data.map((s: any) => ({
          id: s.id,
          name: s.nome,
          email: s.email,
          whatsapp: s.whatsapp || '',
          password: s.password,
          role: s.papel as ManagementRole,
          isActive: s.is_active
        })));
      }

      if (configRes.data) {
        setSystemConfig({
          maxAdsFree: configRes.data.max_ads_free,
          maxAdsPro: configRes.data.max_ads_pro,
          supportWhatsapp: configRes.data.support_whatsapp,
          marketplaceName: configRes.data.marketplace_name,
          marketplaceDescription: configRes.data.marketplace_description,
          instagramUrl: configRes.data.instagram_url,
          youtubeUrl: configRes.data.youtube_url
        });
      }

      if (promoRes.data) {
        setPromotions(promoRes.data.map((p: any) => ({
          id: p.id,
          name: p.nome,
          benefit_type: p.tipo_beneficio as PromotionType,
          benefit_value: p.duracao_dias,
          activation_rule: p.regra_ativacao as ActivationRule,
          is_active: p.is_active,
          coupon_code: p.cupom_code,
          category_id: p.category_id,
          start_date: p.data_inicio,
          end_date: p.data_fim,
          created_at: p.created_at
        })));
      }
    } catch (error) {
      console.error("Falha no carregamento de produção:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    loadData();
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'register') setView('register');
    else if (params.get('view') === 'login') setView('login');
  }, []);

  const getEffectiveVendor = (vendor: VendorProfile): VendorProfile => {
    let effective = { ...vendor };
    if (effective.plan === SubscriptionPlan.PRO && effective.plan_expires_at) {
      if (new Date(effective.plan_expires_at) < new Date()) effective.plan = SubscriptionPlan.FREE;
    }
    return effective;
  };

  const isVerified = (vendor: VendorProfile): boolean => {
    const hasPro = vendor.plan === SubscriptionPlan.PRO;
    const hasActiveBadge = vendor.verified_until ? new Date(vendor.verified_until) > new Date() : false;
    return hasPro || hasActiveBadge;
  };

  const effectiveVendors = useMemo(() => vendors.map(getEffectiveVendor), [vendors]);

  const filteredAds = useMemo(() => {
    const approvedVendorIds = effectiveVendors.filter(v => v.status === VendorStatus.APPROVED).map(v => v.id);
    const search = searchTerm.toLowerCase();
    
    return ads
      .filter(ad => {
        const vendor = effectiveVendors.find(v => v.id === ad.vendor_id);
        const matchesCategory = selectedCategory ? ad.category_id === selectedCategory : true;
        const matchesNeighborhood = selectedNeighborhood ? vendor?.neighborhood_id === selectedNeighborhood : true;
        
        const adTitle = (ad.title || '').toLowerCase();
        const vendorName = (vendor?.name || '').toLowerCase();
        const matchesSearch = adTitle.includes(search) || vendorName.includes(search);
        
        const matchesVerified = verifiedOnly ? (vendor ? isVerified(vendor) : false) : true;
        return approvedVendorIds.includes(ad.vendor_id) && ad.is_active && matchesCategory && matchesSearch && matchesNeighborhood && matchesVerified;
      })
      .sort((a, b) => {
        const vendorA = effectiveVendors.find(v => v.id === a.vendor_id);
        const vendorB = effectiveVendors.find(v => v.id === b.vendor_id);
        const verifiedA = vendorA ? isVerified(vendorA) : false;
        const verifiedB = vendorB ? isVerified(vendorB) : false;
        if (verifiedA && !verifiedB) return -1;
        if (verifiedB && !verifiedA) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
  }, [ads, effectiveVendors, selectedCategory, selectedNeighborhood, searchTerm, verifiedOnly]);

  const handleLogin = async (email: string, pass: string, role: UserRole) => {
    setAuthError(null);
    try {
      if (role === UserRole.ADMIN) {
        const { data } = await supabase.from('equipe_gestao').select('*').eq('email', email.trim()).eq('password', pass).eq('is_active', true).single();
        if (data) {
          setCurrentUser({ id: data.id, role: UserRole.ADMIN, managementRole: data.papel as ManagementRole });
          showToast(`Bem-vindo, gestor ${data.nome}!`, 'success');
          setView('admin');
        } else setAuthError("Credenciais administrativas incorretas.");
      } else {
        const { data } = await supabase.from('perfis_vendedores').select('*').eq('email_contato', email.trim()).eq('password', pass).single();
        if (data) {
          if (data.status === VendorStatus.REJECTED) setAuthError("Perfil desativado temporariamente pela moderação.");
          else {
            setCurrentUser({ id: data.id, role: UserRole.OWNER });
            showToast(`Olá, ${data.nome_responsavel}! Acessando seu painel...`, 'success');
            setView('dashboard');
          }
        } else setAuthError("E-mail ou senha incorretos.");
      }
    } catch (e) { setAuthError("Erro de conexão com o servidor de dados."); }
  };

  const handleRegisterVendor = async (v: VendorProfile): Promise<boolean> => {
    try {
      const { data, error } = await supabase.from('perfis_vendedores').insert({
        id: v.id,
        nome_loja: v.name,
        nome_responsavel: v.responsible,
        slug: v.slug,
        bio: v.bio,
        whatsapp: v.whatsapp,
        instagram: v.instagram,
        avatar_url: v.avatar_url,
        categoria_id: v.category_id,
        bairro_id: v.neighborhood_id,
        email_contato: v.email,
        password: v.password,
        status: 'pending',
        plano: 'free'
      }).select().single();

      if (error) throw error;

      if (data) { 
        await loadData(); 
        setCurrentUser({ id: data.id, role: UserRole.OWNER }); 
        setView('dashboard'); 
        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Erro no registro Supabase:", error);
      showToast(error.message?.includes('unique') ? "Este e-mail já está cadastrado." : "Falha ao criar conta. Tente novamente.", "error");
      return false;
    }
  };

  const handleSaveConfig = async () => {
    try {
      const { error } = await supabase
        .from('configuracoes_sistema')
        .update({
          max_ads_free: systemConfig.maxAdsFree,
          max_ads_pro: systemConfig.maxAdsPro,
          support_whatsapp: systemConfig.supportWhatsapp,
          marketplace_name: systemConfig.marketplaceName,
          marketplace_description: systemConfig.marketplaceDescription,
          instagram_url: systemConfig.instagramUrl,
          youtube_url: systemConfig.youtubeUrl
        })
        .eq('id', 1);

      if (error) throw error;
      showToast("Configurações do sistema atualizadas.", "success");
    } catch (error) {
      console.error("Erro ao salvar config:", error);
      showToast("Falha ao salvar configurações.", "error");
    }
  };

  const handleSavePromotion = async (p: Partial<Promotion>) => {
    try {
      const dataToSave = {
        nome: p.name,
        tipo_beneficio: p.benefit_type,
        duracao_dias: p.benefit_value,
        regra_ativacao: p.activation_rule,
        is_active: p.is_active,
        cupom_code: p.coupon_code,
        category_id: p.category_id,
        data_inicio: p.start_date,
        data_fim: p.end_date
      };

      let error;
      if (p.id) {
        const res = await supabase.from('promocoes').update(dataToSave).eq('id', p.id);
        error = res.error;
      } else {
        const res = await supabase.from('promocoes').insert(dataToSave);
        error = res.error;
      }
      
      if (error) throw error;
      
      showToast("Campanha salva com sucesso!", "success");
      await loadData();
    } catch (error) {
      console.error("Erro ao salvar promoção:", error);
      showToast("Erro ao salvar campanha.", "error");
    }
  };

  const handleUpdateVendorCategory = async (vendorId: string, categoryId: string) => {
    try {
      const { error } = await supabase
        .from('perfis_vendedores')
        .update({ categoria_id: categoryId })
        .eq('id', vendorId);

      if (error) throw error;
      await loadData();
    } catch (error) {
      console.error("Erro ao atualizar categoria do lojista:", error);
      showToast("Falha ao reclassificar parceiro.", "error");
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-brand-navy text-white">
      <Logo className="h-20 mb-10 animate-pulse" />
      <div className="flex gap-3">
        <div className="w-4 h-4 bg-brand-orange rounded-full animate-bounce"></div>
        <div className="w-4 h-4 bg-brand-orange rounded-full animate-bounce [animation-delay:-.3s]"></div>
        <div className="w-4 h-4 bg-brand-orange rounded-full animate-bounce [animation-delay:-.5s]"></div>
      </div>
      <p className="mt-8 text-[10px] font-black uppercase tracking-[0.4em] text-white/40 italic">Iniciando Ambiente de Produção...</p>
    </div>
  );

  return (
    <div className="min-h-screen pb-0 bg-gray-50/50 relative">
      {view === 'buyer' && showPromo && (
        <PromoModal 
          onRegister={() => { setView('register'); setShowPromo(false); }} 
          onClose={() => setShowPromo(false)} 
        />
      )}

      {toast && (
        <div className="fixed top-8 left-1/2 -translate-x-1/2 z-[400] animate-in slide-in-from-top-10 duration-500">
           <div className={`px-8 py-5 rounded-[2.5rem] shadow-2xl border backdrop-blur-xl flex items-center gap-4 ${
             toast.type === 'success' ? 'bg-green-500/90 border-green-400 text-white' :
             toast.type === 'error' ? 'bg-red-500/90 border-red-400 text-white' :
             'bg-brand-navy/90 border-white/20 text-white'
           }`}>
             {toast.type === 'success' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/></svg>}
             {toast.type === 'error' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>}
             <p className="text-xs font-black uppercase tracking-widest">{toast.message}</p>
           </div>
        </div>
      )}

      {view === 'login' && <Login onLogin={handleLogin} onBack={() => setView('buyer')} error={authError} />}
      {view === 'register' && (
        <VendorRegistration 
          neighborhoods={neighborhoods} 
          categories={categories} 
          onComplete={handleRegisterVendor} 
          onCancel={() => setView('buyer')}
          onToast={showToast}
        />
      )}
      
      {view === 'admin' && currentUser?.role === UserRole.ADMIN && (
        <AdminPanel 
          vendors={vendors} ads={ads} neighborhoods={neighborhoods} categories={categories} 
          systemConfig={systemConfig} managementRole={currentUser.managementRole} staffList={staff}
          currentUserId={currentUser.id} promotions={promotions}
          onUpdateConfig={(config) => { setSystemConfig(config); }} 
          onSaveConfig={handleSaveConfig}
          onSavePromotion={handleSavePromotion}
          onApprove={async (id) => { 
            await supabase.from('perfis_vendedores').update({ status: VendorStatus.APPROVED }).eq('id', id); 
            showToast("Loja aprovada com sucesso!", 'success');
            await loadData(); 
          }} 
          onReject={async (id) => { 
            await supabase.from('perfis_vendedores').update({ status: VendorStatus.REJECTED }).eq('id', id); 
            showToast("Operação suspensa.", 'error');
            await loadData(); 
          }} 
          onUpdatePlan={async (id, plan, daysOrDate) => { 
            let exp;
            if (typeof daysOrDate === 'string') {
              exp = new Date(daysOrDate);
            } else {
              exp = new Date();
              exp.setDate(exp.getDate() + (daysOrDate || 30));
            }
            await supabase.from('perfis_vendedores').update({ plano: plan, plan_expires_at: plan === 'pro' ? exp.toISOString() : null }).eq('id', id); 
            showToast(`Vencimento do plano ${plan.toUpperCase()} atualizado.`, 'success');
            await loadData(); 
          }} 
          onUpdateVerificationBadge={async (id, days) => {
            const unt = new Date(); unt.setDate(unt.getDate() + days);
            await supabase.from('perfis_vendedores').update({ verified_until: days > 0 ? unt.toISOString() : null }).eq('id', id); 
            showToast(days > 0 ? "Selo verificado concedido." : "Selo removido.", 'info');
            await loadData();
          }}
          onUpdateVendorCategory={handleUpdateVendorCategory}
          onAddNeighborhood={async (nome, zona) => { 
            await supabase.from('bairros').insert({ nome, zona_aeroporto: zona, slug: nome.toLowerCase() }); 
            showToast(`Bairro ${nome} criado.`, 'success');
            await loadData(); 
          }} 
          onUpdateNeighborhood={async (id, nome, zona) => { 
            await supabase.from('bairros').update({ nome, zona_aeroporto: zona }).eq('id', id); 
            await loadData(); 
          }}
          onAddCategory={async (nome, icone) => { 
            await supabase.from('categorias').insert({ nome, icone, slug: nome.toLowerCase() }); 
            showToast(`Categoria ${nome} criada.`, 'success');
            await loadData(); 
          }} 
          onUpdateCategory={async (id, nome, icone) => { 
            await supabase.from('categorias').update({ nome, icone }).eq('id', id); 
            await loadData(); 
          }}
          onUpdateStaff={async () => { 
            await loadData(); 
          }} 
          onBack={() => { setCurrentUser(null); setView('buyer'); }}
        />
      )}
      
      {view === 'dashboard' && currentUser && (
        <EntrepreneurDashboard 
          vendor={getEffectiveVendor(vendors.find(v => v.id === currentUser.id)!)} 
          ads={ads.filter(ad => ad.vendor_id === currentUser.id)} 
          categories={categories} 
          neighborhoods={neighborhoods} 
          allVendors={vendors} 
          allAds={ads} 
          systemConfig={systemConfig}
          activePromotions={promotions.filter(p => p.is_active)}
          onToast={showToast}
          onAddAd={async (a) => { 
            const { error } = await supabase.from('anuncios').insert({ 
              titulo: a.title,
              descricao: a.description,
              preco: a.price,
              preco_sob_consulta: a.price_on_request,
              categoria_id: a.category_id,
              imagens: a.images,
              tags: a.tags,
              vendedor_id: currentUser.id, 
              is_active: true 
            }); 
            if (error) {
              console.error("ERRO SUPABASE (onAddAd):", JSON.stringify(error, null, 2));
              showToast(`Falha: ${error.code} - ${error.message}`, 'error');
            } else {
              showToast("Sua oferta está no ar!", 'success');
              await loadData(); 
            }
          }} 
          onUpdateAd={async (id, a) => { 
            const { error } = await supabase.from('anuncios').update({
              titulo: a.title,
              descricao: a.description,
              preco: a.price,
              preco_sob_consulta: a.price_on_request,
              categoria_id: a.category_id,
              imagens: a.images,
              tags: a.tags,
              is_active: a.is_active
            }).eq('id', id); 
            if (error) {
              console.error("ERRO SUPABASE (onUpdateAd):", JSON.stringify(error, null, 2));
              showToast(`Erro ${error.code}: Falha ao salvar alterações.`, 'error');
            } else {
              showToast("Alterações salvas.", 'success');
              await loadData(); 
            }
          }} 
          onDeleteAd={async (id) => { 
            await supabase.from('anuncios').delete().eq('id', id); 
            showToast("Oferta removida.", 'info');
            await loadData(); 
          }}
          onUpdateVendor={async (v) => { 
            const { error } = await supabase.from('perfis_vendedores').update({
              nome_loja: v.name,
              nome_responsavel: v.responsible,
              whatsapp: v.whatsapp,
              instagram: v.instagram,
              avatar_url: v.avatar_url,
              bairro_id: v.neighborhood_id,
              categoria_id: v.category_id,
              bio: v.bio,
              email_verified: v.email_verified,
              password: v.password
            }).eq('id', currentUser.id); 
            if (error) {
              console.error("ERRO SUPABASE (onUpdateVendor):", JSON.stringify(error, null, 2));
              showToast(`Erro ${error.code}: Perfil não atualizado.`, 'error');
            } else {
              showToast("Perfil da loja atualizado.", 'success');
              await loadData(); 
            }
          }} 
          onBack={() => { setCurrentUser(null); setView('buyer'); }} 
        />
      )}

      {view === 'vendor' && viewingVendor && (
        <VendorProfileView vendor={getEffectiveVendor(viewingVendor)} isVerifiedBadge={isVerified(getEffectiveVendor(viewingVendor))} ads={ads.filter(a => a.vendor_id === viewingVendor.id && a.is_active)} neighborhood={neighborhoods.find(n => n.id === viewingVendor.neighborhood_id)} onBack={() => setView('buyer')} onAdClick={setSelectedAd} />
      )}

      {view === 'buyer' && (
        <>
          <nav className="bg-white/90 backdrop-blur-xl py-4 sticky top-0 z-50 shadow-sm px-3 sm:px-6 border-b border-gray-100 flex items-center justify-between">
              <div className="cursor-pointer" onClick={() => { setView('buyer'); setSelectedCategory(null); setSelectedNeighborhood(null); setSearchTerm(''); scrollToTop(); }}><Logo className="h-10 sm:h-12" /></div>
              <div className="flex gap-2 sm:gap-4 items-center">
                {currentUser ? (
                   <button onClick={() => setView(currentUser.role === UserRole.ADMIN ? 'admin' : 'dashboard')} className="text-brand-navy text-[10px] font-black uppercase tracking-widest px-2 sm:px-4 py-2 hover:text-brand-orange transition-all flex items-center gap-2">
                     <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                     Painel
                   </button>
                ) : (
                   <button onClick={() => setView('login')} className="text-brand-navy text-[10px] font-black uppercase tracking-widest px-2 sm:px-4 py-2 hover:text-brand-orange transition-all">Entrar</button>
                )}
                <button onClick={() => setView('register')} className="bg-brand-orange text-white text-[10px] px-4 sm:px-6 py-3.5 rounded-2xl font-black hover:scale-105 active:scale-95 transition-all uppercase tracking-widest shadow-xl shadow-brand-orange/20">Anunciar Grátis</button>
              </div>
          </nav>

          <header className="bg-brand-gradient pt-24 pb-40 px-6 relative text-center overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
              <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[100%] bg-white blur-[150px] rounded-full"></div>
            </div>
            <div className="max-w-4xl mx-auto relative z-10 animate-in fade-in slide-in-from-bottom-6 duration-700">
              <div className="inline-block bg-white/10 px-6 py-2 rounded-full border border-white/10 text-white text-[9px] font-black uppercase tracking-[0.3em] mb-8 backdrop-blur-md">📍 Guarulhos • Vitrine Digital Local</div>
              <h2 className="text-5xl md:text-7xl font-title font-black text-white mb-8 leading-[1] tracking-tighter italic">O comércio da cidade <br/><span className="text-brand-orange italic drop-shadow-lg">na sua mão.</span></h2>
              <p className="text-white/70 text-base sm:text-xl max-w-2xl mx-auto font-medium leading-relaxed">Conectamos você aos melhores empreendedores de Guarulhos. Tudo direto pelo WhatsApp.</p>
            </div>
          </header>

          <main className="max-w-[1700px] mx-auto px-6 -mt-24 relative z-20">
            <FilterBar neighborhoods={neighborhoods} categories={categories} selectedCategory={selectedCategory} selectedNeighborhood={selectedNeighborhood} searchTerm={searchTerm} verifiedOnly={verifiedOnly} onCategoryChange={setSelectedCategory} onNeighborhoodChange={setSelectedNeighborhood} onSearchChange={setSearchTerm} onVerifiedOnlyChange={setVerifiedOnly} />
            
            {/* Promoção Pioneiros */}
            <section className="mt-12 bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-gray-100 flex flex-col lg:flex-row animate-in fade-in zoom-in duration-1000">
               <div className="flex-1 p-10 sm:p-14 bg-brand-navy text-white relative">
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                  </div>
                  <span className="text-brand-orange text-[10px] font-black uppercase tracking-[0.4em] block mb-4">Oportunidade Limitada</span>
                  <h3 className="text-3xl sm:text-5xl font-title font-black uppercase italic tracking-tighter leading-none mb-6 italic">Apoie o Projeto e Ganhe <br/><span className="text-brand-orange">6 Meses de Plano PRO</span></h3>
                  <p className="text-white/60 text-sm font-medium leading-relaxed max-w-md mb-8">Estamos lançando a maior vitrine de Guarulhos. Seja um dos primeiros parceiros a acreditar na força do comércio local e receba benefícios exclusivos para sempre.</p>
                  <button onClick={() => setView('register')} className="bg-white text-brand-navy font-black px-10 py-5 rounded-2xl text-[11px] uppercase tracking-widest hover:bg-brand-orange hover:text-white transition-all shadow-xl shadow-white/5 flex items-center gap-3 group">
                    Garantir meu Benefício
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
                  </button>
               </div>
               <div className="flex-1 p-10 sm:p-14 grid grid-cols-1 sm:grid-cols-2 gap-8 bg-gray-50">
                  <div className="space-y-3">
                    <div className="w-10 h-10 bg-brand-blue/10 rounded-xl flex items-center justify-center text-brand-blue font-black">01</div>
                    <h4 className="text-[11px] font-black text-brand-navy uppercase tracking-widest">Destaque Absoluto</h4>
                    <p className="text-[10px] text-gray-400 font-bold leading-relaxed uppercase tracking-tight">Sua loja sempre aparece no topo das buscas da cidade.</p>
                  </div>
                  <div className="space-y-3">
                    <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center text-brand-orange font-black">02</div>
                    <h4 className="text-[11px] font-black text-brand-navy uppercase tracking-widest">Selo Verificado</h4>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">Badge de confiança que gera até 3x mais cliques no Whats.</p>
                  </div>
                  <div className="space-y-3">
                    <div className="w-10 h-10 bg-brand-blue/10 rounded-xl flex items-center justify-center text-brand-blue font-black">03</div>
                    <h4 className="text-[11px] font-black text-brand-navy uppercase tracking-widest">Catálogo Sem Limites</h4>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">Cadastre quantos produtos quiser sem nenhuma restrição.</p>
                  </div>
                  <div className="space-y-3">
                    <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center text-brand-orange font-black">04</div>
                    <h4 className="text-[11px] font-black text-brand-navy uppercase tracking-widest">Métricas de Elite</h4>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">Saiba exatamente quantos clientes te chamaram por dia.</p>
                  </div>
               </div>
            </section>

            <div className="mt-20 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-8">
              {filteredAds.slice(0, visibleCount).map(ad => {
                const vendor = vendors.find(v => v.id === ad.vendor_id);
                return <div key={ad.id} onClick={() => setSelectedAd(ad)} className="animate-in fade-in zoom-in duration-500"><AdCard ad={ad} vendor={vendor} isVerifiedBadge={vendor ? isVerified(vendor) : false} neighborhood={neighborhoods.find(n => n.id === vendor?.neighborhood_id)} /></div>;
              })}
            </div>

            {filteredAds.length === 0 && (
              <div className="text-center py-40 bg-white rounded-[4rem] border border-gray-100 shadow-sm animate-in zoom-in duration-300">
                <h4 className="text-2xl font-black text-brand-navy uppercase italic mb-2 tracking-tighter italic">Nenhuma oferta disponível</h4>
                <p className="text-gray-400 text-sm font-medium">Ajuste os filtros para encontrar novos parceiros locais.</p>
              </div>
            )}

            {filteredAds.length > visibleCount && (
              <div className="mt-20 text-center">
                <button onClick={() => setVisibleCount(prev => prev + 18)} className="bg-brand-navy text-white font-black px-16 py-6 rounded-3xl hover:bg-brand-blue transition-all shadow-2xl uppercase tracking-widest text-[11px] hover:scale-105 active:scale-95">Ver mais ofertas</button>
              </div>
            )}
          </main>

          {selectedAd && <ProductModal ad={selectedAd} allAds={ads} vendor={vendors.find(v => v.id === selectedAd.vendor_id)} neighborhood={neighborhoods.find(n => n.id === vendors.find(v => v.id === selectedAd.vendor_id)?.neighborhood_id)} onClose={() => setSelectedAd(null)} onAdClick={setSelectedAd} onViewProfile={(v) => {setViewingVendor(v); setView('vendor'); setSelectedAd(null);}} />}
          
          <InfoModal type={infoModalType} onClose={() => setInfoModalType(null)} />

          <footer className="mt-40 bg-brand-navy relative overflow-hidden border-t border-white/5">
            <div className="max-w-[1700px] mx-auto px-8 md:px-12 pt-24 pb-12 relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-20 mb-20">
                <div className="space-y-8">
                  <Logo className="h-14 cursor-pointer" variant="light" onClick={() => { setView('buyer'); scrollToTop(); }} />
                  <p className="text-white/50 text-xs font-medium leading-relaxed max-w-xs">{systemConfig.marketplaceDescription}</p>
                </div>
                <div>
                  <h4 className="text-white text-[10px] font-black uppercase tracking-[0.3em] mb-10 opacity-40">Navegação</h4>
                  <ul className="space-y-5">
                    <li><button onClick={() => { setView('buyer'); setSelectedCategory(null); setSelectedNeighborhood(null); setSearchTerm(''); scrollToTop(); }} className="text-white/60 hover:text-brand-orange text-[10px] font-black uppercase tracking-widest transition-all">Início</button></li>
                    <li><button onClick={() => { setView('register'); scrollToTop(); }} className="text-white/60 hover:text-brand-orange text-[10px] font-black uppercase tracking-widest transition-all">Anunciar Loja</button></li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white text-[10px] font-black uppercase tracking-[0.3em] mb-10 opacity-40">Suporte</h4>
                  <ul className="space-y-5">
                    <li><button onClick={() => setInfoModalType('terms')} className="text-white/60 hover:text-brand-orange text-[10px] font-black uppercase tracking-widest transition-all">Termos</button></li>
                    <li><button onClick={() => setInfoModalType('how-it-works')} className="text-white/60 hover:text-brand-orange text-[10px] font-black uppercase tracking-widest transition-all">Como Funciona</button></li>
                  </ul>
                </div>
                <div className="bg-white/5 p-10 rounded-[3rem] border border-white/5 text-left">
                   <h4 className="text-white text-[10px] font-black uppercase tracking-[0.3em] mb-6">Central de Ajuda</h4>
                   <button onClick={() => window.open(`https://wa.me/${systemConfig.supportWhatsapp}`, '_blank')} className="text-brand-orange text-[10px] font-black uppercase tracking-widest hover:underline">Falar no WhatsApp</button>
                </div>
              </div>
            </div>
          </footer>
        </>
      )}
    </div>
  );
};

export default App;
